'use client';

import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { useState } from 'react';
import { toast } from 'sonner';
import { Send, Mail } from 'lucide-react';

import { sendContactEmail } from '@/actions/contact';
import TurnstileField from '@/components/newsletter/TurnstileField';
import { useTurnstileSubmit } from '@/hooks/useTurnstileSubmit';
import { getAttributionForSubmit } from '@/lib/attribution';
import { LEAD_LIMITS } from '@/lib/website-leads/validation';

type ContactFormProps = {
  heading?: string;
  description?: string;
  email?: string; // display-only contact email under the form
  className?: string;
};

/** CTA id of the page the form sits on, stored with the message. */
function contactCta(pathname: string): string | undefined {
  if (pathname === '/') return 'contact_home';
  if (pathname === '/contact') return 'contact_page';
  return undefined;
}

export default function ContactForm({
  heading = 'Send us a message',
  description,
  email = 'info@blueberries.app',
  className,
}: ContactFormProps) {
  const [name, setName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  // Honeypot: hidden from people, filled in by naive bots.
  const [website, setWebsite] = useState('');
  // The bot check loads on the first focus, so pages that only show the form
  // do not load Cloudflare's script.
  const [checkStarted, setCheckStarted] = useState(false);
  const [botCheckFailures, setBotCheckFailures] = useState(0);
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  const turnstile = useTurnstileSubmit(token => {
    void send(token);
  });

  async function send(turnstileToken: string) {
    setStatus('loading');
    try {
      const { pathname, search, origin } = window.location;
      const data = await sendContactEmail({
        name,
        email: userEmail,
        message,
        phone,
        company,
        cta: contactCta(pathname),
        pageUrl: `${origin}${pathname}${search}`,
        ...getAttributionForSubmit(),
        turnstileToken,
        website,
      });

      if (!data.ok) {
        if (data.reason === 'bot_check') {
          setBotCheckFailures(count => count + 1);
        }
        throw new Error(data.error || 'Failed to send');
      }

      setStatus('success');
      toast('Success!', { description: 'Thanks! Your message has been sent successfully.' });
      setName('');
      setUserEmail('');
      setMessage('');
      setPhone('');
      setCompany('');
    } catch (err) {
      setStatus('error');
      toast.error('Delivery Failed', {
        description:
          err instanceof Error && err.message
            ? err.message
            : 'Something went wrong sending your message.',
      });
    } finally {
      // The token was spent on this attempt.
      turnstile.reset();
    }
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'loading' || turnstile.waiting) return;
    setCheckStarted(true);
    turnstile.request();
  }

  const busy = status === 'loading' || turnstile.waiting;

  return (
    <div className={`bg-white rounded-2xl shadow-xl border border-slate-100 p-8 sm:p-10 ${className}`}>
      <div className="flex flex-col items-center justify-center mb-8">
        <div className="h-12 w-12 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mb-4 border border-purple-100 shadow-sm">
          <Mail size={24} strokeWidth={2} />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 text-center tracking-tight">{heading}</h2>
        {description && (
          <p className="text-slate-500 text-center mt-2 max-w-lg mx-auto text-sm">
            {description}
          </p>
        )}
      </div>

      <form
        className="space-y-5"
        onSubmit={handleSubmit}
        onFocus={() => setCheckStarted(true)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-700">Full Name *</label>
            <input
              type="text"
              placeholder="e.g. Jane Doe"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              maxLength={LEAD_LIMITS.name}
              className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white transition-all placeholder:text-slate-400"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-700">Email Address *</label>
            <input
              type="email"
              placeholder="jane@company.com"
              value={userEmail}
              onChange={e => setUserEmail(e.target.value)}
              required
              maxLength={LEAD_LIMITS.email}
              className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white transition-all placeholder:text-slate-400"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-700">Company</label>
            <input
              type="text"
              placeholder="Company Name"
              value={company}
              onChange={e => setCompany(e.target.value)}
              maxLength={LEAD_LIMITS.company}
              className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white transition-all placeholder:text-slate-400"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-700">Phone Number</label>
            <input
              type="text"
              placeholder="+1 (555) 000-0000"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              maxLength={LEAD_LIMITS.phone}
              className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white transition-all placeholder:text-slate-400"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-700">Your Message</label>
          <textarea
            placeholder="How can we help you?"
            rows={5}
            value={message}
            onChange={e => setMessage(e.target.value)}
            maxLength={LEAD_LIMITS.message}
            className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white transition-all resize-none placeholder:text-slate-400"
          />
        </div>

        {/* Takes no room (and adds no gap) unless Cloudflare asks the visitor
            to interact. */}
        <div className="!mt-0">
          {/* Off-screen rather than display:none, which some bots skip. */}
          <div
            aria-hidden="true"
            className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden"
          >
            <label>
              Website
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={e => setWebsite(e.target.value)}
              />
            </label>
          </div>
          {checkStarted && (
            <TurnstileField
              action="contact_form"
              appearance="interaction-only"
              resetSignal={turnstile.challengeKey}
              onToken={turnstile.onToken}
            />
          )}
        </div>

        <Button
          className="w-full bg-purple-700 hover:bg-purple-800 text-white rounded-xl h-12 text-sm font-bold shadow-md shadow-purple-200 transition-all active:scale-[0.98] mt-2 gap-2"
          disabled={status === 'loading'}
          aria-busy={busy}
        >
          {busy ? (
            'Sending message...'
          ) : (
            <>
              Send Message
              <Send size={16} />
            </>
          )}
        </Button>

        {turnstile.waiting && (
          <p role="status" className="text-center text-[13px] text-slate-500">
            Checking your browser, one second...
          </p>
        )}
        {botCheckFailures >= 2 && (
          <p role="alert" className="text-center text-sm font-medium text-slate-700">
            Please email us at{' '}
            <Link
              href={`mailto:${email}`}
              className="text-purple-700 font-semibold hover:text-purple-800 hover:underline"
            >
              {email}
            </Link>
          </p>
        )}

        <p className="text-center text-xs leading-relaxed text-slate-500">
          We&apos;ll use these details to reply to your message. See our{' '}
          <Link
            href="/privacy"
            className="font-medium text-purple-700 hover:underline"
          >
            Privacy Policy
          </Link>
          .
        </p>
      </form>

      <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col items-center gap-2">
        <p className="text-sm text-slate-500">Need specific help? Reach us directly at</p>
        <Link
          href={`mailto:${email}`}
          className="text-purple-700 font-semibold hover:text-purple-800 hover:underline transition-colors"
        >
          {email}
        </Link>
      </div>
    </div>
  );
}
